import React, { useState, useEffect, useContext } from 'react';
import { array, object, string } from 'prop-types';
import style from './Articles.module.scss';
import Filter from '@components/Filter/Filter';
import Button, { VARIANT } from '@components/Button/Button.js';
import SearchInput from '@components/SearchInput/SearchInput';
import ArticlePreview from '@components/ArticlePreview';
import { useDebounce } from '@hooks';
import { parseString } from '@helpers';
import LangContext from '@contexts';
import { langPath } from '@helpers';
import { Link } from 'gatsby';
import { RichText } from 'prismic-reactjs';

const numberToRender = 8;
const COUNTER_STEP = 4;

const Articles = ({
  title,
  articlesList,
  buttontext,
  subtitle,
  placeholder,
  filtersbuttontext,
  isCategory,
  categoryTitle,
  newsearch,
  tagnewsearch,
  filteredArticles,
  filteredArticlesWithTag,
}) => {
  const [counter, setCounter] = useState(numberToRender);
  const [list, setList] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState([]);
  const [dateRange, setDateRange] = useState();
  const debounceSearchResult = useDebounce(search, 500);
  const currentLang = useContext(LangContext);

  const convertCategoryName = (category) => {
    return category
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' & ');
  };

  let tagList = [];
  articlesList.forEach(({ node }) => {
    tagList = [...tagList, ...node.tags];
  });
  const uniqTagList = [...new Set(tagList)];

  useEffect(() => {
    if (
      isCategory &&
      isCategory.uid !== 'legal-news' &&
      isCategory.type === 'category'
    ) {
      setSelectedTag([isCategory.uid.replaceAll('-', ' ').toLowerCase()]);
    } else if (isCategory?.uid === 'legal-news') {
      setSelectedTag([convertCategoryName(isCategory.uid).toLowerCase()]);
    } else {
      setSelectedTag([]);
    }
  }, [isCategory]);

  useEffect(() => {
    const filteredList = articlesList.filter(({ node }) => {
      const { data: postData, tags } = node;
      const { title, date } = postData;
      const filterBySearch = debounceSearchResult
        ? parseString(title.richText)
            .toLowerCase()
            .includes(debounceSearchResult.toLowerCase())
        : true;
      const filterByDate = dateRange
        ? dateRange.startDate <= Date.parse(date) &&
          Date.parse(date) <= dateRange.endDate
        : true;

      const filterByTag =
        selectedTag.length > 0
          ? selectedTag.some((tag) =>
              tags.join(' ').toLowerCase().includes(tag)
            )
          : true;

      return filterBySearch && filterByDate && filterByTag;
    });
    setList(filteredList.slice(0, counter));
  }, [debounceSearchResult, selectedTag, dateRange, articlesList, counter]);

  const handleClickLoadMore = () => {
    setCounter((prevCounter) => prevCounter + COUNTER_STEP);
  };

  const handleInputChange = (e) => {
    setSearch(e.target.value);
  };

  const handleTagChange = (tag) => {
    setSelectedTag(tag);
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
  };

  const displayList =
    newsearch || tagnewsearch
      ? newsearch
        ? filteredArticles
        : filteredArticlesWithTag
      : list;

  return (
    <section className={style.articles}>
      <div className={style.wrapper}>
        <div className={style.title}>{subtitle && subtitle.text}</div>
        <div className={style.search}>
          <SearchInput onChange={handleInputChange} placeholder={placeholder} />
        </div>
        <div className={style.filter}>
          <Filter
            tagList={uniqTagList}
            tagChange={handleTagChange}
            dateChange={handleDateRangeChange}
            filtersbuttontext={filtersbuttontext}
          />
        </div>
      </div>
      <div className={style.maintitle}>
        {categoryTitle ? (
          <RichText render={categoryTitle.richText} />
        ) : (
          <RichText render={title.richText} />
        )}
      </div>

      <div className={style.list}>
        {displayList.slice(0, counter).map((item) => (
          <ArticlePreview {...item} key={item.node.uid} />
        ))}
      </div>

      {articlesList.length > counter && displayList.length >= counter && (
        <div className={style.buttonWrapper}>
          <Button
            variant={VARIANT.TRANSPARENT}
            click={handleClickLoadMore}
            element="button"
            fullWidth
          >
            {buttontext.text}
          </Button>
        </div>
      )}
      {!displayList.length && (
        <div className={style.wrappernoList}>
          <h5>{`No blogposts were found with this tag 😥`}</h5>
          <div className={style.buttonWrapper}>
            <Link to={`${langPath(currentLang)}/blog`}>
              <Button variant={VARIANT.TRANSPARENT} element="button" fullWidth>
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
      )}
    </section>
  );
};

Articles.propTypes = {
  title: object,
  articlesList: array.isRequired,
  buttontext: object,
  subtitle: object,
  placeholder: object,
  filtersbuttontext: object,
  isCategory: object,
  categoryTitle: object,
  newsearch: string,
  tagnewsearch: string,
  filteredArticles: array,
  filteredArticlesWithTag: array,
};

export default Articles;
